import { useEffect, useRef, useState } from "react";

type SelectProps = {
    options: { id: string | number; label: string }[];
    selected: Array<string | number>;
    onChange: (values: Array<string | number>) => void;
    placeholder?: string;
    hasError?: boolean;
    showChipLabelTooltip?: boolean;
};

export const OptionsSelectInput = ({
    options,
    selected,
    onChange,
    placeholder = "Select...",
    hasError = false,
    showChipLabelTooltip = false,
}: SelectProps) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            const target = e.target;
            if (!ref.current || !(target instanceof Node)) {
                return;
            }

            if (!ref.current.contains(target)) {
                setOpen(false);
            }
        };
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClick);
        document.addEventListener("keydown", handleEsc);
        return () => {
            document.removeEventListener("mousedown", handleClick);
            document.removeEventListener("keydown", handleEsc);
        };
    }, []);

    const filtered = options.filter(o =>
        o.label.toLowerCase().includes(search.toLowerCase())
    );

    const toggle = (id: string | number) => {
        const idStr = String(id);
        const isSelected = selected.some(s => String(s) === idStr);
        let next;
        if (isSelected) {
            next = selected.filter(x => String(x) !== idStr);
        } else {
            next = [...selected, id];
        }
        onChange(next);
    };

    let errorClass = "";
    if (hasError) {
        errorClass = "form__control--error";
    }
    const selectClassName = `select2-like ${errorClass}`;

    return (
        <div
            className={selectClassName}
            ref={ref}
        >
            <div
                role="button"
                tabIndex={0}
                className="select2-input"
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => setOpen(prev => !prev)}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setOpen(prev => !prev);
                    }
                }}
            >
                {selected.map(id => {
                    const item = options.find(o => String(o.id) === String(id));
                    const chipLabel = item?.label ?? String(id);
                    return (
                        <span key={`selected-${String(id)}`} className="chip">
                            <span className="chip__label">{chipLabel}</span>
                            {showChipLabelTooltip && (
                                <div className="sidebar-tree__tooltip chip__tooltip">
                                    {chipLabel}
                                </div>
                            )}
                            <span
                                className="chip__remove"
                                aria-hidden="true"
                                onClick={e => {
                                    e.stopPropagation();
                                    toggle(id);
                                }}
                                onMouseDown={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                            />
                        </span>
                    );
                })}

                {selected.length === 0 && (
                    <span className="select2-input__placeholder">
                        {placeholder}
                    </span>
                )}
            </div>

            {open && (
                <div className="select2-dropdown">
                    <input
                        type="text"
                        placeholder="Search..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="select2-dropdown__search"
                        autoFocus
                    />

                    <ul>
                        {filtered.map(item => {
                            const isSelected = selected.some(s => String(s) === String(item.id));
                            return (
                                <li
                                    key={`option-${item.id}`}
                                    className="select2-list-item"
                                >
                                    <button
                                        type="button"
                                        onClick={() => toggle(item.id)}
                                        aria-pressed={isSelected}
                                        className={isSelected ? 'is-selected' : ''}
                                    >
                                        <span className="select2-list-item__label">
                                            {item.label}
                                        </span>
                                       {isSelected && ( <span className="se-icon--acknowledged" aria-hidden="true"></span> )}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
};